import { useMemo, useRef, useState, type WheelEvent, type PointerEvent } from 'react';
import type { DataGap, RawProfilePoint } from '@/types/profile';

interface ProfileGraphProps {
  allPoints: RawProfilePoint[];
  leadIn: RawProfilePoint[];
  trimmed: RawProfilePoint[];
  leadOut: RawProfilePoint[];
  gaps: DataGap[];
  unit: string;
}

const PADDING = { top: 24, right: 24, bottom: 36, left: 56 };
const VIEW_W = 1000;
const VIEW_H = 420;

function pathFor(
  points: RawProfilePoint[],
  xScale: (v: number) => number,
  yScale: (v: number) => number,
): string {
  if (points.length === 0) return '';
  return points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xScale(p.position).toFixed(2)} ${yScale(p.radius).toFixed(2)}`)
    .join(' ');
}

export function ProfileGraph({ allPoints, leadIn, trimmed, leadOut, gaps, unit }: ProfileGraphProps) {
  const [viewBox, setViewBox] = useState({ x: 0, y: 0, w: VIEW_W, h: VIEW_H });
  const dragState = useRef<{ startX: number; startY: number; vb: typeof viewBox } | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const { xScale, yScale, xMin, xMax, yMin, yMax } = useMemo(() => {
    const positions = allPoints.map((p) => p.position);
    const radii = allPoints.map((p) => p.radius);
    const xMin = Math.min(...positions);
    const xMax = Math.max(...positions);
    const yMin = 0;
    const yMax = Math.max(...radii) * 1.1 || 1;

    const innerW = VIEW_W - PADDING.left - PADDING.right;
    const innerH = VIEW_H - PADDING.top - PADDING.bottom;

    const xScale = (v: number) =>
      PADDING.left + ((v - xMin) / (xMax - xMin || 1)) * innerW;
    // Flip Y so radius increases upward.
    const yScale = (v: number) =>
      VIEW_H - PADDING.bottom - ((v - yMin) / (yMax - yMin || 1)) * innerH;

    return { xScale, yScale, xMin, xMax, yMin, yMax };
  }, [allPoints]);

  function handleWheel(e: WheelEvent<SVGSVGElement>) {
    e.preventDefault();
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const mouseXFrac = (e.clientX - rect.left) / rect.width;
    const mouseYFrac = (e.clientY - rect.top) / rect.height;

    const zoomFactor = e.deltaY > 0 ? 1.12 : 1 / 1.12;
    setViewBox((vb) => {
      const newW = Math.min(VIEW_W * 4, Math.max(VIEW_W * 0.05, vb.w * zoomFactor));
      const newH = Math.min(VIEW_H * 4, Math.max(VIEW_H * 0.05, vb.h * zoomFactor));
      const mouseX = vb.x + mouseXFrac * vb.w;
      const mouseY = vb.y + mouseYFrac * vb.h;
      const newX = mouseX - mouseXFrac * newW;
      const newY = mouseY - mouseYFrac * newH;
      return { x: newX, y: newY, w: newW, h: newH };
    });
  }

  function handlePointerDown(e: PointerEvent<SVGSVGElement>) {
    (e.target as Element).setPointerCapture(e.pointerId);
    dragState.current = { startX: e.clientX, startY: e.clientY, vb: viewBox };
  }

  function handlePointerMove(e: PointerEvent<SVGSVGElement>) {
    if (!dragState.current || !svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const { startX, startY, vb } = dragState.current;
    const dx = ((e.clientX - startX) / rect.width) * vb.w;
    const dy = ((e.clientY - startY) / rect.height) * vb.h;
    setViewBox({ ...vb, x: vb.x - dx, y: vb.y - dy });
  }

  function handlePointerUp() {
    dragState.current = null;
  }

  function resetZoom() {
    setViewBox({ x: 0, y: 0, w: VIEW_W, h: VIEW_H });
  }

  const leadInPath = pathFor(leadIn.length ? [...leadIn, trimmed[0] ?? leadIn[leadIn.length - 1]] : [], xScale, yScale);
  const trimmedPath = pathFor(trimmed, xScale, yScale);
  const leadOutPath = pathFor(
    leadOut.length ? [trimmed[trimmed.length - 1] ?? leadOut[0], ...leadOut] : [],
    xScale,
    yScale,
  );

  // Group contiguous reconstructed points into their own path segments so
  // they can be drawn with a distinct style, bordered by one real point on
  // each side for visual continuity with the trusted curve.
  const reconstructedPaths = useMemo(() => {
    const segments: string[] = [];
    let current: RawProfilePoint[] = [];
    for (let i = 0; i < trimmed.length; i++) {
      const p = trimmed[i];
      if (p.reconstructed) {
        if (current.length === 0 && i > 0) current.push(trimmed[i - 1]);
        current.push(p);
      } else if (current.length > 0) {
        current.push(p);
        segments.push(pathFor(current, xScale, yScale));
        current = [];
      }
    }
    if (current.length > 0) segments.push(pathFor(current, xScale, yScale));
    return segments;
  }, [trimmed, xScale, yScale]);

  const filledGaps = gaps.filter((g) => g.filled);
  const unresolvedGaps = gaps.filter((g) => !g.filled);

  // Simple axis ticks.
  const xTicks = 6;
  const yTicks = 4;
  const xTickValues = Array.from({ length: xTicks + 1 }, (_, i) => xMin + ((xMax - xMin) * i) / xTicks);
  const yTickValues = Array.from({ length: yTicks + 1 }, (_, i) => yMin + ((yMax - yMin) * i) / yTicks);

  return (
    <div>
      <div className="graph-toolbar">
        <span className="legend-chip">
          <span className="legend-swatch" style={{ background: 'var(--text-tertiary)' }} />
          Lead-in / Lead-out
        </span>
        <span className="legend-chip">
          <span className="legend-swatch" style={{ background: 'var(--accent)' }} />
          Trimmed profile (used for 3D)
        </span>
        {filledGaps.length > 0 && (
          <span className="legend-chip">
            <span className="legend-swatch" style={{ background: 'var(--steel)' }} />
            Reconstructed (estimate, not machine data)
          </span>
        )}
        {unresolvedGaps.length > 0 && (
          <span className="legend-chip">
            <span className="legend-swatch" style={{ background: 'var(--danger)' }} />
            Possible missing geometry
          </span>
        )}
        <button className="btn btn-ghost" style={{ width: 'auto', padding: '5px 10px', marginLeft: 'auto' }} onClick={resetZoom}>
          Reset Zoom
        </button>
      </div>
      <svg
        ref={svgRef}
        viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.w} ${viewBox.h}`}
        style={{
          width: '100%',
          height: 460,
          background: 'var(--bg-panel-raised)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 10,
          cursor: 'grab',
          touchAction: 'none',
        }}
        onWheel={handleWheel}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        {/* Grid + axes */}
        {xTickValues.map((v, i) => (
          <g key={`x-${i}`}>
            <line
              x1={xScale(v)}
              y1={PADDING.top}
              x2={xScale(v)}
              y2={VIEW_H - PADDING.bottom}
              stroke="var(--border-subtle)"
              strokeWidth={1}
            />
            <text x={xScale(v)} y={VIEW_H - PADDING.bottom + 16} fontSize={10} fill="var(--text-tertiary)" textAnchor="middle" fontFamily="var(--font-mono)">
              {v.toFixed(0)}
            </text>
          </g>
        ))}
        {yTickValues.map((v, i) => (
          <g key={`y-${i}`}>
            <line
              x1={PADDING.left}
              y1={yScale(v)}
              x2={VIEW_W - PADDING.right}
              y2={yScale(v)}
              stroke="var(--border-subtle)"
              strokeWidth={1}
            />
            <text x={PADDING.left - 8} y={yScale(v) + 3} fontSize={10} fill="var(--text-tertiary)" textAnchor="end" fontFamily="var(--font-mono)">
              {v.toFixed(1)}
            </text>
          </g>
        ))}

        <text
          x={(PADDING.left + VIEW_W - PADDING.right) / 2}
          y={VIEW_H - 4}
          fontSize={10.5}
          fill="var(--text-tertiary)"
          textAnchor="middle"
        >
          Position ({unit})
        </text>
        <text
          x={14}
          y={(PADDING.top + VIEW_H - PADDING.bottom) / 2}
          fontSize={10.5}
          fill="var(--text-tertiary)"
          textAnchor="middle"
          transform={`rotate(-90 14 ${(PADDING.top + VIEW_H - PADDING.bottom) / 2})`}
        >
          Radius ({unit})
        </text>

        {leadInPath && (
          <path d={leadInPath} fill="none" stroke="var(--text-tertiary)" strokeWidth={1.5} strokeDasharray="3 3" />
        )}
        {leadOutPath && (
          <path d={leadOutPath} fill="none" stroke="var(--text-tertiary)" strokeWidth={1.5} strokeDasharray="3 3" />
        )}
        {trimmedPath && <path d={trimmedPath} fill="none" stroke="var(--accent)" strokeWidth={2} />}

        {reconstructedPaths.map((d, i) => (
          <path
            key={`recon-${i}`}
            d={d}
            fill="none"
            stroke="var(--steel)"
            strokeWidth={2.5}
            strokeDasharray="6 3"
          />
        ))}

        {filledGaps.map((g, i) => (
          <rect
            key={`filled-${i}`}
            x={xScale(g.fromPosition)}
            y={PADDING.top}
            width={Math.max(1, xScale(g.toPosition) - xScale(g.fromPosition))}
            height={VIEW_H - PADDING.top - PADDING.bottom}
            fill="var(--steel)"
            opacity={0.08}
          />
        ))}

        {unresolvedGaps.map((g, i) => (
          <g key={`gap-${i}`}>
            <rect
              x={xScale(g.fromPosition)}
              y={PADDING.top}
              width={Math.max(1, xScale(g.toPosition) - xScale(g.fromPosition))}
              height={VIEW_H - PADDING.top - PADDING.bottom}
              fill="var(--danger)"
              opacity={0.12}
            />
            <line
              x1={xScale(g.fromPosition)}
              y1={yScale(g.fromRadius)}
              x2={xScale(g.toPosition)}
              y2={yScale(g.toRadius)}
              stroke="var(--danger)"
              strokeWidth={1.5}
              strokeDasharray="2 3"
            />
          </g>
        ))}
      </svg>
    </div>
  );
}
